/* istanbul ignore file */

export const EventType = {
  BOOKING_CREATED: 'BOOKING_CREATED',
  RESERVATION_CREATED: 'RESERVATION_CREATED',
} as const

export type EventTypeKey = keyof typeof EventType

export interface EventPayloads {
  [EventType.BOOKING_CREATED]: unknown
  [EventType.RESERVATION_CREATED]: unknown
}

const ALLOWED_TARGET_ORIGINS = ['https://portal-playground.distribusion.com', 'https://portal.distribusion.com']

/**
 * Sends a structured event to the parent window using postMessage.
 * This utility is designed to be used when the application is running inside
 * an iframe and needs to communicate with its parent window.
 *
 * @template T - The type of event value (must be a valid EventType value)
 * @param {T} eventType - The type of event (from EventType constant)
 * @param {EventPayloads[T]} payload - The payload to send with the event
 * @returns {Promise<boolean>} - Promise resolving to true if message was sent successfully
 *
 * @example
 * // Basic usage with just the target origin:
 * sendEvent(EventType.BOOKING_CREATED, bookingResponse, { targetOrigin: 'https://portal.example.com' });
 *
 * // Advanced usage with options object:
 * sendEvent(EventType.BOOKING_CREATED, bookingResponse, {
 *   targetOrigin: 'https://portal.example.com'
 * });
 */
export async function postMessage<T extends (typeof EventType)[EventTypeKey]>(
  eventType: T,
  payload: EventPayloads[T],
  targetOrigin: string | null = null,
): Promise<boolean> {
  if (window.parent === window) {
    console.warn('postMessage: Not in an iframe, no parent window to communicate with')
    return false
  }
  if (!targetOrigin || !ALLOWED_TARGET_ORIGINS.includes(targetOrigin)) {
    console.error(`postMessage: Invalid target origin "${targetOrigin}"`)
    return false
  }
  try {
    const message = {
      type: eventType,
      payload,
      timestamp: new Date().toISOString(),
    }

    window.parent.postMessage(message, targetOrigin)
    return true
  } catch (error) {
    // Log error if message sending fails
    console.error(`postMessage: Failed to send ${eventType} event to parent`, {
      error,
      targetOrigin,
      eventType,
    })
    return false
  }
}
